import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export interface CallTask {
  id: number
  title: string
  is_completed: boolean
  call_log: number | null
  created_at: string
}

interface TasksResponse {
  tasks?: CallTask[]
  results?: CallTask[]
}

export function useCallTasks(callId: number | null) {
  return useQuery({
    queryKey: ['call-tasks', callId],
    queryFn: async () => {
      if (!callId) return [] as CallTask[]
      const { data } = await api.get<TasksResponse>('/api/tasks/', {
        params: { call_log: callId },
      })
      return data.tasks || data.results || []
    },
    enabled: !!callId,
  })
}

export function useContactTasks(phoneNumber: string | null) {
  return useQuery({
    queryKey: ['contact-tasks', phoneNumber],
    queryFn: async () => {
      if (!phoneNumber) return [] as CallTask[]
      const { data } = await api.get<TasksResponse>('/api/tasks/', {
        params: { phone_number: phoneNumber },
      })
      return data.tasks || data.results || []
    },
    enabled: !!phoneNumber,
  })
}

export function useCreateContactTask(phoneNumber: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (title: string) => {
      const { data } = await api.post<CallTask>('/api/tasks/', {
        title,
        call_log: null,
        phone_number: phoneNumber,
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-tasks', phoneNumber] })
    },
  })
}

export function useToggleContactTask(phoneNumber: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (task: CallTask) => {
      const url = task.is_completed
        ? `/api/tasks/${task.id}/reopen/`
        : `/api/tasks/${task.id}/complete/`
      await api.post(url)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-tasks', phoneNumber] })
    },
  })
}

export function useDeleteContactTask(phoneNumber: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (taskId: number) => {
      await api.delete(`/api/tasks/${taskId}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-tasks', phoneNumber] })
    },
  })
}

export function useCreateTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { title: string; call_log: number }) => {
      const { data } = await api.post<CallTask>('/api/tasks/', input)
      return data
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['call-tasks', vars.call_log] })
    },
  })
}

export function useToggleTask(callId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (task: CallTask) => {
      const url = task.is_completed
        ? `/api/tasks/${task.id}/reopen/`
        : `/api/tasks/${task.id}/complete/`
      await api.post(url)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['call-tasks', callId] })
    },
  })
}

export function useDeleteTask(callId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (taskId: number) => {
      await api.delete(`/api/tasks/${taskId}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['call-tasks', callId] })
    },
  })
}
